import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { ChevronDown, ChevronUp, FileJson, Plus, RefreshCw, Upload } from 'lucide-react'
import { DualGateStatus, XeroImportForm, XeroManualForm } from '../../components/licensing'
import type { Entitlement, ImportResponse, XeroInvoiceState } from '../../types'
import type { ImportMode } from './use-license-activation'

export function LoadingState(): ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#5c6e94]" />
    </div>
  )
}

export function PageHeader({
  onRefresh,
  loading,
  t,
}: {
  onRefresh: () => void
  loading: boolean
  t: TFunction<'admin'>
}): ReactElement {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-stone-900 dark:text-stone-100">
          {t('licenseActivation.title')}
        </h1>
        <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
          {t('licenseActivation.description')}
        </p>
      </div>
      <button
        onClick={onRefresh}
        disabled={loading}
        className="inline-flex items-center gap-2 rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50 disabled:opacity-50 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-700"
      >
        <RefreshCw className="h-4 w-4" />
        {t('actions.refresh')}
      </button>
    </div>
  )
}

export function ErrorAlert({
  error,
  onDismiss,
  t,
}: {
  error: string
  onDismiss: () => void
  t: TFunction<'admin'>
}): ReactElement {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
      <p className="text-red-600 dark:text-red-400">{error}</p>
      <button
        onClick={onDismiss}
        className="mt-2 text-sm text-red-700 underline hover:no-underline dark:text-red-300"
      >
        {t('licenseActivation.dismiss')}
      </button>
    </div>
  )
}

interface ImportSectionProps {
  showImportSection: boolean
  setShowImportSection: (show: boolean) => void
  importMode: ImportMode
  setImportMode: (mode: ImportMode) => void
  onImportSuccess: (result: ImportResponse) => void
  onImportError: (err: Error) => void
  t: TFunction<'admin'>
}

function ImportModeButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean
  icon: ReactElement
  label: string
  onClick: () => void
}): ReactElement {
  const baseClass =
    'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors'
  const activeClass = 'bg-[#5c6e94] text-white'
  const inactiveClass =
    'border border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700'
  return (
    <button onClick={onClick} className={`${baseClass} ${active ? activeClass : inactiveClass}`}>
      {icon}
      {label}
    </button>
  )
}

function ImportSectionToggle({
  showImportSection,
  setShowImportSection,
  t,
}: Pick<ImportSectionProps, 'showImportSection' | 'setShowImportSection' | 't'>): ReactElement {
  return (
    <button
      onClick={() => {
        setShowImportSection(!showImportSection)
      }}
      className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-stone-50 dark:hover:bg-stone-700/50"
    >
      <div className="flex items-center gap-3">
        <Upload className="h-5 w-5 text-stone-500 dark:text-stone-400" />
        <div>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">
            {t('licenseActivation.importXeroData')}
          </h2>
          <p className="text-sm text-stone-600 dark:text-stone-400">
            {t('licenseActivation.importDescription')}
          </p>
        </div>
      </div>
      {showImportSection ? (
        <ChevronUp className="h-5 w-5 text-stone-400" />
      ) : (
        <ChevronDown className="h-5 w-5 text-stone-400" />
      )}
    </button>
  )
}

export function ImportSection(props: ImportSectionProps): ReactElement {
  const { showImportSection, importMode, setImportMode, onImportSuccess, onImportError, t } = props
  return (
    <div className="overflow-hidden rounded-xl border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800">
      <ImportSectionToggle
        showImportSection={showImportSection}
        setShowImportSection={props.setShowImportSection}
        t={t}
      />
      {showImportSection && (
        <div className="border-t border-stone-200 px-6 pb-6 dark:border-stone-700">
          <div className="flex gap-3 pt-4 pb-4">
            <ImportModeButton
              active={importMode === 'json'}
              icon={<FileJson className="h-4 w-4" />}
              label={t('licenseActivation.importJson')}
              onClick={() => {
                setImportMode(importMode === 'json' ? 'none' : 'json')
              }}
            />
            <ImportModeButton
              active={importMode === 'manual'}
              icon={<Plus className="h-4 w-4" />}
              label={t('licenseActivation.manualEntry')}
              onClick={() => {
                setImportMode(importMode === 'manual' ? 'none' : 'manual')
              }}
            />
          </div>
          {importMode === 'json' && (
            <XeroImportForm onSuccess={onImportSuccess} onError={onImportError} />
          )}
          {importMode === 'manual' && (
            <XeroManualForm onSuccess={onImportSuccess} onError={onImportError} />
          )}
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: string }): ReactElement {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    revoked: 'bg-stone-100 text-stone-700 dark:bg-stone-700 dark:text-stone-400',
  }
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${colors[status] ?? colors.pending ?? ''}`}
    >
      {status}
    </span>
  )
}

function GateIndicator({ passed, label }: { passed: boolean; label: string }): ReactElement {
  return (
    <span
      className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium ${
        passed
          ? 'bg-[#4e5e82]/10 text-[#5c6e94] dark:bg-[#8a9bba]/15 dark:text-[#8a9bba]'
          : 'bg-stone-100 text-stone-500 dark:bg-stone-700 dark:text-stone-400'
      }`}
    >
      {label}
    </span>
  )
}

interface EntitlementRowProps {
  entitlement: Entitlement
  isSelected: boolean
  hasXeroData: boolean
  onSelect: () => void
}

function EntitlementRow({
  entitlement,
  isSelected,
  hasXeroData,
  onSelect,
}: EntitlementRowProps): ReactElement {
  return (
    <button
      onClick={onSelect}
      className={`w-full px-4 py-3 text-left transition-colors ${
        isSelected
          ? 'border-l-2 border-[#4e5e82] bg-[#4e5e82]/5 dark:bg-[#8a9bba]/10'
          : 'hover:bg-stone-50 dark:hover:bg-stone-700/50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-stone-900 dark:text-white">
            {entitlement.planTierGranted}
          </p>
          <p className="text-xs text-stone-500 dark:text-stone-400">
            {entitlement.seatsUsed}/{entitlement.seatsGranted} seats
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasXeroData && (
            <span className="inline-flex items-center rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              Xero
            </span>
          )}
          <StatusBadge status={entitlement.status} />
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1">
        <GateIndicator passed={entitlement.paymentGatePassed} label="P" />
        <span className="text-stone-300 dark:text-stone-600">→</span>
        <GateIndicator passed={entitlement.licenseGatePassed} label="L" />
        <span className="text-stone-300 dark:text-stone-600">→</span>
        <GateIndicator passed={entitlement.status === 'active'} label="A" />
      </div>
    </button>
  )
}

interface EntitlementsListProps {
  entitlements: Entitlement[]
  xeroStates: Map<string, XeroInvoiceState>
  selectedEntitlementId: string | null
  setSelectedEntitlementId: (id: string) => void
  t: TFunction<'admin'>
}

export function EntitlementsList({
  entitlements,
  xeroStates,
  selectedEntitlementId,
  setSelectedEntitlementId,
  t,
}: EntitlementsListProps): ReactElement {
  return (
    <div className="lg:col-span-1">
      <div className="rounded-xl border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800">
        <div className="border-b border-stone-200 px-4 py-3 dark:border-stone-700">
          <h3 className="font-semibold text-stone-900 dark:text-white">
            {t('licenseActivation.entitlements')}
          </h3>
          <p className="text-xs text-stone-500 dark:text-stone-400">
            {t('licenseActivation.totalEntitlements', { count: entitlements.length })}
          </p>
        </div>
        <div className="max-h-96 divide-y divide-stone-200 overflow-y-auto dark:divide-stone-700">
          {entitlements.length === 0 ? (
            <div className="p-4 text-center text-sm text-stone-500 dark:text-stone-400">
              {t('licenseActivation.noEntitlementsFound')}
            </div>
          ) : (
            entitlements.map((entitlement) => (
              <EntitlementRow
                key={entitlement.id}
                entitlement={entitlement}
                isSelected={selectedEntitlementId === entitlement.id}
                hasXeroData={xeroStates.has(entitlement.id)}
                onSelect={() => {
                  setSelectedEntitlementId(entitlement.id)
                }}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

interface ActivationStatusProps {
  selectedEntitlement: Entitlement | undefined
  selectedXeroState: XeroInvoiceState | null
  isProcessing: boolean
  onPassPaymentGate: () => void
  onPassLicenseGate: () => void
  onActivate: () => void
  t: TFunction<'admin'>
}

export function ActivationStatus(props: ActivationStatusProps): ReactElement {
  const {
    selectedEntitlement,
    selectedXeroState,
    isProcessing,
    onPassPaymentGate,
    onPassLicenseGate,
    onActivate,
    t,
  } = props
  return (
    <div className="lg:col-span-2">
      {selectedEntitlement ? (
        <DualGateStatus
          entitlement={selectedEntitlement}
          xeroInvoiceState={selectedXeroState}
          onPassPaymentGate={onPassPaymentGate}
          onPassLicenseGate={onPassLicenseGate}
          onActivate={onActivate}
          isLoading={isProcessing}
        />
      ) : (
        <div className="rounded-xl border border-stone-200 bg-white p-8 text-center dark:border-stone-700 dark:bg-stone-800">
          <p className="text-stone-500 dark:text-stone-400">
            {t('licenseActivation.selectEntitlement')}
          </p>
        </div>
      )}
    </div>
  )
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string
  value: number
  color?: 'green' | 'amber' | 'lime' | 'red'
}): ReactElement {
  const valueColors = {
    green: 'text-green-600 dark:text-green-400',
    amber: 'text-amber-600 dark:text-amber-400',
    lime: 'text-[#5c6e94] dark:text-[#8a9bba]',
    red: 'text-red-600 dark:text-red-400',
  }
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4 dark:border-stone-700 dark:bg-stone-800">
      <p className="text-sm text-stone-500 dark:text-stone-400">{label}</p>
      <p
        className={`text-2xl font-semibold ${color ? valueColors[color] : 'text-stone-900 dark:text-stone-100'}`}
      >
        {value}
      </p>
    </div>
  )
}

export function StatsSummary({
  entitlements,
  t,
}: {
  entitlements: Entitlement[]
  t: TFunction<'admin'>
}): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
      <StatCard label={t('licenseActivation.totalEntitlementsLabel')} value={entitlements.length} />
      <StatCard
        label={t('licenseActivation.active')}
        value={entitlements.filter((e) => e.status === 'active').length}
        color="green"
      />
      <StatCard
        label={t('licenseActivation.pending')}
        value={entitlements.filter((e) => e.status === 'pending').length}
        color="amber"
      />
      <StatCard
        label={t('licenseActivation.paymentGatePassed')}
        value={entitlements.filter((e) => e.paymentGatePassed).length}
        color="lime"
      />
      <StatCard
        label={t('licenseActivation.bothGatesPassed')}
        value={entitlements.filter((e) => e.gatesPassed).length}
        color="lime"
      />
    </div>
  )
}
